import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { 
  Building, Building2, Users, User, Plus, Edit, Trash2,
  Loader2, AlertCircle, Info
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { organizationService } from '../../api/organizationService';
import { departmentService } from '../../api/departmentService';
import { teamService } from '../../api/teamService';
import { userManagementService } from '../../api/userManagementService';
import { tenantService } from '../../api/tenantService';

/**
 * EntityDetailsPane Component
 * Displays entity details with tabs: Overview, Departments, Teams, Roles
 * 
 * @param {Object} entity - Selected entity from tree
 * @param {Function} onEdit - Callback when edit button is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {Function} onAddChild - Callback when add child button is clicked
 */
const EntityDetailsPane = ({ entity, onEdit, onDelete, onAddChild }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null); // Store loaded user data separately

  useEffect(() => {
    if (entity) {
      loadEntityData();
    } else {
      resetData();
    }
  }, [entity]);

  const resetData = () => {
    setStats(null);
    setDepartments([]);
    setTeams([]);
    setUsers([]);
    setRoles([]);
    setUserData(null);
    setActiveTab('overview');
    setError(null);
  };

  const loadEntityData = async () => {
    if (!entity) return;
    
    setLoading(true);
    setError(null);
    
    try {
      switch (entity.type) {
        case 'tenant':
          await loadTenantData();
          break;
        case 'organization':
          await loadOrganizationData();
          break;
        case 'department':
          await loadDepartmentData();
          break;
        case 'team':
          await loadTeamData();
          break;
        case 'user':
          await loadUserData();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error loading entity data:', err);
      setError('Failed to load entity data');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantData = async () => {
    const statsResponse = await tenantService.getTenantStatistics(entity.data.tenantId);
    setStats(statsResponse.data?.data || {});
    
    const orgsResponse = await organizationService.getOrganizationsByTenant(entity.data.tenantId);
    setDepartments(orgsResponse.data?.data || []);
  };

  const loadOrganizationData = async () => {
    const orgResponse = await organizationService.getOrganizationById(entity.data.organizationId);
    const org = orgResponse.data?.data || {};
    
    setStats({
      departments: org.departmentCount || 0,
      users: org.userCount || 0,
      teams: org.teamCount || 0
    });
    
    const deptsResponse = await departmentService.getDepartmentsByOrganization(entity.data.organizationId);
    setDepartments(deptsResponse.data?.data || []);
  };

  const loadDepartmentData = async () => {
    const deptResponse = await departmentService.getDepartmentById(entity.data.departmentId);
    const dept = deptResponse.data?.data || {};
    
    setStats({
      teams: dept.teamCount || 0,
      users: dept.userCount || 0
    });
    
    const teamsResponse = await teamService.getTeamsByDepartment(entity.data.departmentId);
    setTeams(teamsResponse.data?.data || []);
    
    const usersResponse = await departmentService.getDepartmentUsers(entity.data.departmentId);
    setUsers(usersResponse.data?.data || []);
  };

  const loadTeamData = async () => {
    const teamResponse = await teamService.getTeamById(entity.data.teamId);
    const team = teamResponse.data?.data || {};
    
    setStats({
      members: team.memberCount || 0
    });
    
    const membersResponse = await teamService.getTeamMembers(entity.data.teamId);
    setUsers(membersResponse.data?.data || []);
  };

  const loadUserData = async () => {
    // Handle both userId and id fields - entity.id might be "user-user-005" or "user-005"
    // entity.data should have the original user object with userId
    let userId = entity.data?.userId || entity.data?.id;
    
    // If not found, try extracting from entity.id (format: "user-user-005" or "user-005")
    if (!userId && entity.id) {
      const idMatch = entity.id.match(/user-?(.+)$/);
      userId = idMatch ? idMatch[1] : entity.id.replace('user-', '');
    }
    
    // If entity.data already has complete user info, use it directly
    if (entity.data && (entity.data.fullName || entity.data.FullName) && (entity.data.email || entity.data.Email)) {
      // Set userData from entity.data
      setUserData(entity.data);
      
      // Use entity data directly, but still try to load teams and roles
      setStats({
        roles: entity.data?.roleCount || (entity.data?.roles ? entity.data.roles.length : 0) || 0,
        teams: entity.data?.teamCount || (entity.data?.teams ? entity.data.teams.length : 0) || 0
      });
      
      // Set teams and roles from entity.data if available (but convert to proper format)
      if (entity.data.teams && Array.isArray(entity.data.teams)) {
        // If teams are just IDs, we'll load them from API, otherwise use as-is
        const teamIds = entity.data.teams.filter(t => typeof t === 'string');
        if (teamIds.length === entity.data.teams.length) {
          // All are IDs, will load from API below
        } else {
          // Already have team objects
          setTeams(entity.data.teams);
        }
      }
      if (entity.data.roles && Array.isArray(entity.data.roles)) {
        // If roles are just strings, convert to objects
        if (typeof entity.data.roles[0] === 'string') {
          setRoles(entity.data.roles.map(roleName => ({
            roleId: `role-${roleName.toLowerCase().replace(/\s+/g, '-')}`,
            roleName: roleName,
            roleCode: roleName.toUpperCase().replace(/\s+/g, '_'),
            isActive: true
          })));
        } else {
          setRoles(entity.data.roles);
        }
      }
      
      // Try to load additional data from API if userId is available
      if (userId) {
        try {
          const [rolesResponse, teamsResponse] = await Promise.allSettled([
            userManagementService.getUserRoles(userId),
            userManagementService.getUserTeams(userId)
          ]);
          
          if (rolesResponse.status === 'fulfilled' && rolesResponse.value?.data?.data) {
            setRoles(rolesResponse.value.data.data);
          }
          if (teamsResponse.status === 'fulfilled' && teamsResponse.value?.data?.data) {
            setTeams(teamsResponse.value.data.data);
          }
        } catch (err) {
          console.warn('Could not load additional user data from API:', err);
          // Continue with entity.data values
        }
      }
      return;
    }
    
    if (!userId) {
      setError('User ID not found');
      return;
    }
    
    try {
      const userResponse = await userManagementService.getUserById(userId);
      const user = userResponse.data?.data || {};
      
      // Merge API data with entity.data and store in userData state
      const mergedUserData = { ...entity.data, ...user };
      setUserData(mergedUserData);
      
      setStats({
        roles: user.roleCount || (user.roles ? user.roles.length : 0) || 0,
        teams: user.teamCount || (user.teams ? user.teams.length : 0) || 0
      });
      
      const rolesResponse = await userManagementService.getUserRoles(userId);
      setRoles(rolesResponse.data?.data || []);
      
      const teamsResponse = await userManagementService.getUserTeams(userId);
      setTeams(teamsResponse.data?.data || []);
    } catch (err) {
      console.error('Error loading user data:', err);
      // Set default stats from entity data if available
      setStats({
        roles: entity.data?.roleCount || (entity.data?.roles ? entity.data.roles.length : 0) || 0,
        teams: entity.data?.teamCount || (entity.data?.teams ? entity.data.teams.length : 0) || 0
      });
      
      // Use entity.data teams and roles as fallback
      if (entity.data?.teams && Array.isArray(entity.data.teams)) {
        setTeams(entity.data.teams);
      }
      if (entity.data?.roles && Array.isArray(entity.data.roles)) {
        setRoles(entity.data.roles);
      }
      
      // Use entity.data as userData fallback
      setUserData(entity.data);
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'tenant':
        return <Building className="w-5 h-5 text-amber-600" />;
      case 'organization':
        return <Building2 className="w-5 h-5 text-blue-600" />;
      case 'department':
        return <Users className="w-5 h-5 text-green-600" />;
      case 'team':
        return <Users className="w-5 h-5 text-purple-600" />;
      case 'user':
        return <User className="w-5 h-5 text-gray-600" />;
      default:
        return null;
    }
  };

  const getEntityName = () => {
    if (!entity) return '';
    return entity.name || entity.data?.tenantName || entity.data?.organizationName || 
           entity.data?.departmentName || entity.data?.teamName || 
           entity.data?.fullName || entity.data?.username || 'Unknown';
  };

  const getAvailableTabs = () => {
    if (!entity) return [];
    
    const tabs = [{ id: 'overview', label: 'Overview' }];
    
    switch (entity.type) {
      case 'tenant':
        tabs.push({ id: 'organizations', label: 'Organizations' });
        break;
      case 'organization':
        tabs.push({ id: 'departments', label: 'Departments' });
        tabs.push({ id: 'users', label: 'Users' });
        break;
      case 'department':
        tabs.push({ id: 'teams', label: 'Teams' });
        tabs.push({ id: 'users', label: 'Users' });
        break;
      case 'team':
        tabs.push({ id: 'members', label: 'Members' });
        break;
      case 'user':
        tabs.push({ id: 'roles', label: 'Roles' });
        tabs.push({ id: 'teams', label: 'Teams' });
        break;
      default:
        break;
    }
    
    return tabs;
  };

  if (!entity) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select an entity from the tree to view details</p>
        </div>
      </div>
    );
  }

  const tabs = getAvailableTabs();
  const Icon = getEntityIcon(entity.type);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {Icon}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{getEntityName()}</h2>
              <p className="text-sm text-gray-500 capitalize">{entity.type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit && onEdit(entity)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete && onDelete(entity)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-white">
        <div className="flex gap-1 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  {stats && Object.entries(stats).map(([key, value]) => (
                    <Card key={key}>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold">{value}</div>
                        <div className="text-sm text-gray-500 capitalize">{key}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-3">
                      {entity.type === 'user' ? (() => {
                        // Use userData if available (from API), otherwise fall back to entity.data
                        const displayData = userData || entity.data || {};
                        const isActiveValue = displayData.isActive !== undefined ? displayData.isActive : displayData.IsActive !== undefined ? displayData.IsActive : true;
                        
                        return (
                          <>
                            <div className="flex justify-between py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-500">FullName</dt>
                              <dd className="text-sm font-semibold text-gray-900">
                                {displayData.fullName || displayData.FullName || displayData.name || entity.name || 'N/A'}
                              </dd>
                            </div>
                            <div className="flex justify-between py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-500">Username</dt>
                              <dd className="text-sm text-gray-900">
                                {displayData.username || displayData.userName || displayData.Username || 'N/A'}
                              </dd>
                            </div>
                            <div className="flex justify-between py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-500">Email</dt>
                              <dd className="text-sm text-gray-900">
                                {displayData.email || displayData.Email || 'N/A'}
                              </dd>
                            </div>
                            <div className="flex justify-between py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-500">IsActive</dt>
                              <dd className="text-sm text-gray-900">
                                <Badge variant={isActiveValue !== false ? 'success' : 'secondary'}>
                                  {String(isActiveValue !== false ? 'true' : 'false')}
                                </Badge>
                              </dd>
                            </div>
                            {displayData.phone && (
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                                <dd className="text-sm text-gray-900">{displayData.phone}</dd>
                              </div>
                            )}
                            {displayData.status && (
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <dt className="text-sm font-medium text-gray-500">Status</dt>
                                <dd className="text-sm text-gray-900">
                                  <Badge variant={displayData.status === 'active' ? 'success' : 'secondary'}>
                                    {displayData.status}
                                  </Badge>
                                </dd>
                              </div>
                            )}
                            {displayData.departmentName && (
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <dt className="text-sm font-medium text-gray-500">Department</dt>
                                <dd className="text-sm text-gray-900">{displayData.departmentName}</dd>
                              </div>
                            )}
                            {displayData.tenantName && (
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <dt className="text-sm font-medium text-gray-500">Tenant</dt>
                                <dd className="text-sm text-gray-900">{displayData.tenantName}</dd>
                              </div>
                            )}
                          </>
                        );
                      })() : (
                        // Generic entity fields
                        entity.data && Object.entries(entity.data).map(([key, value]) => {
                          // Skip complex objects, IDs, counts, and nested data
                          if (typeof value === 'object' || 
                              key.includes('Id') || 
                              key.includes('Count') || 
                              key === 'teams' || 
                              key === 'roles' ||
                              key === 'userId' ||
                              key === 'organizationId' ||
                              key === 'departmentId' ||
                              key === 'teamId' ||
                              key === 'tenantId') return null;
                          
                          // Format key name for display
                          const formattedKey = key
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/^./, str => str.toUpperCase())
                            .trim();
                          
                          return (
                            <div key={key} className="flex justify-between py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-500">{formattedKey}</dt>
                              <dd className="text-sm text-gray-900">
                                {value === true || value === false ? (
                                  <Badge variant={value ? 'success' : 'secondary'}>
                                    {String(value)}
                                  </Badge>
                                ) : (
                                  String(value || 'N/A')
                                )}
                              </dd>
                            </div>
                          );
                        })
                      )}
                    </dl>
                  </CardContent>
                </Card>

                {/* Show Teams for Users in Overview */}
                {entity.type === 'user' && teams.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Teams</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {teams.map(team => (
                          <div key={team.teamId || team.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <h4 className="font-medium text-gray-900">{team.teamName || team.name}</h4>
                              {team.description && (
                                <p className="text-sm text-gray-500">{team.description}</p>
                              )}
                            </div>
                            <Badge variant={team.isActive ? 'success' : 'secondary'}>
                              {team.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Show Roles for Users in Overview */}
                {entity.type === 'user' && roles.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Roles</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {roles.map(role => (
                          <div key={role.roleId || role.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <h4 className="font-medium text-gray-900">{role.roleName || role.name}</h4>
                              {role.roleCode && (
                                <p className="text-sm text-gray-500">{role.roleCode}</p>
                              )}
                            </div>
                            <Badge variant={role.isActive !== false ? 'success' : 'secondary'}>
                              {role.isActive !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'departments' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Departments</h3>
                  <Button size="sm" onClick={() => onAddChild && onAddChild('department')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Department
                  </Button>
                </div>
                <div className="space-y-2">
                  {departments.map(dept => (
                    <Card key={dept.departmentId || dept.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{dept.departmentName || dept.name}</h4>
                            <p className="text-sm text-gray-500">{dept.description}</p>
                          </div>
                          <Badge variant={dept.isActive ? 'success' : 'secondary'}>
                            {dept.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'teams' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Teams</h3>
                  <Button size="sm" onClick={() => onAddChild && onAddChild('team')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Team
                  </Button>
                </div>
                <div className="space-y-2">
                  {teams.map(team => (
                    <Card key={team.teamId || team.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{team.teamName || team.name}</h4>
                            <p className="text-sm text-gray-500">{team.description}</p>
                          </div>
                          <Badge variant={team.isActive ? 'success' : 'secondary'}>
                            {team.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Team Members</h3>
                  <Button size="sm" onClick={() => onAddChild && onAddChild('member')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </div>
                <div className="space-y-2">
                  {users.map(user => (
                    <Card key={user.userId || user.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{user.fullName || user.username}</h4>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <Badge variant={user.isActive ? 'success' : 'secondary'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Users</h3>
                  <Button size="sm" onClick={() => onAddChild && onAddChild('user')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </div>
                <div className="space-y-2">
                  {users.map(user => (
                    <Card key={user.userId || user.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{user.fullName || user.username}</h4>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <Badge variant={user.isActive ? 'success' : 'secondary'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'roles' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Roles</h3>
                <div className="space-y-2">
                  {roles.map(role => (
                    <Card key={role.roleId || role.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{role.roleName || role.name}</h4>
                            <p className="text-sm text-gray-500">{role.roleCode || role.code}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'organizations' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Organizations</h3>
                  <Button size="sm" onClick={() => onAddChild && onAddChild('organization')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Organization
                  </Button>
                </div>
                <div className="space-y-2">
                  {departments.map(org => (
                    <Card key={org.organizationId || org.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{org.organizationName || org.name}</h4>
                            <p className="text-sm text-gray-500">{org.description}</p>
                          </div>
                          <Badge variant={org.isActive ? 'success' : 'secondary'}>
                            {org.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EntityDetailsPane;

